import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { stashInviteToken } from '../auth/inviteToken';

/**
 * Landing point for a staff-issued invite link: /claim?token=…
 *
 * This page never binds anything itself. It stashes the token and hands off to
 * signup or login; the bind happens server-side in `redeem_client_invite`,
 * which reads the client id off the invite row rather than trusting anything
 * the browser sends.
 *
 * The token is deliberately not logged, not put in a query string we control,
 * and not rendered.
 */
const ClaimPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const [problem, setProblem] = useState('');

  useEffect(() => {
    if (isLoading) return;

    const token = new URLSearchParams(location.search).get('token');
    if (!token) {
      setProblem('This invite link is missing its code. Please use the link from your email exactly as it was sent.');
      return;
    }

    stashInviteToken(token);

    // Already signed in — AuthContext redeems on the next session tick.
    navigate(isAuthenticated ? '/dashboard' : '/signup', { replace: true });
  }, [isLoading, isAuthenticated, location.search, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        {problem ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">We couldn&apos;t read that invite</h1>
            <p className="text-gray-600">{problem}</p>
          </>
        ) : (
          <>
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Opening your invite…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default ClaimPage;
